import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Panel, Toolbar } from '../components/Page';
import { SelectField } from '../components/Form';
import { Chip } from '../components/Chips';
import { CardSkeleton, EmptyState, ErrorState, TableSkeleton } from '../components/Feedback';
import { TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { Pagination } from '../components/Pagination';
import { useAuth } from '../lib/auth';
import { partnerDeliveries } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { formatDateTime, formatRelative } from '../lib/format';
import type { HandshakeDeliveryDto } from '../lib/types';

type DeliveryColumn = 'from' | 'theirDocument' | 'status' | 'acknowledged' | 'became' | 'received';

function deliverySortValue(d: HandshakeDeliveryDto, key: DeliveryColumn): string | number {
  switch (key) {
    case 'from':
      return d.counterpartyCompanyName ?? d.sellerCompanyName ?? '';
    case 'theirDocument':
      return d.postedSalesInvoiceNo ?? d.bcSourceDocumentNo ?? '';
    case 'status':
      return d.status;
    case 'acknowledged':
      return d.acknowledged ? 1 : 0;
    case 'became':
      return d.qboDocNumber ?? '';
    case 'received':
      return d.receivedUtc;
    default:
      return '';
  }
}

// Mirrors Domain/PartnerEntities.cs HandshakeIntakeStatus exactly. This is the INBOUND enum — it
// has Blocked, not Refused (Refused only exists on the outbound side's status, PartnerOutbound.tsx)
// — the two were conflated here once, which meant filtering by "Refused" matched nothing, a
// Blocked delivery got a neutral chip, and canRetry() below never fired for it even though the
// backend explicitly retries Blocked rows (RetryAsync, HandshakeInboundService.cs).
const STATUS_OPTIONS = [
  { value: 'Received', label: 'Received — waiting to be processed' },
  { value: 'Deferred', label: 'Deferred — receiving is paused' },
  { value: 'Processing', label: 'Processing' },
  { value: 'Completed', label: 'Completed — handed to a document' },
  { value: 'Failed', label: 'Failed — a retry may fix it' },
  { value: 'Blocked', label: 'Blocked — the connection did not authorise it' },
];

function statusTone(status: string): 'good' | 'warn' | 'bad' | 'neutral' {
  switch (status) {
    case 'Completed':
      return 'good';
    case 'Failed':
    case 'Blocked':
      return 'bad';
    case 'Deferred':
    case 'Processing':
      return 'warn';
    default:
      return 'neutral';
  }
}

/** Failed (an error a retry may fix) and Blocked (connection authority refused it) both re-enter
 * through Received on the backend — see RetryAsync's comment in HandshakeInboundService.cs. */
function canRetry(status: string): boolean {
  return status === 'Failed' || status === 'Blocked';
}

/**
 * WHAT ARRIVED FROM A TRADING PARTNER, AND WHERE IT WENT.
 *
 * The inbound path is deliberately careful and, until now, completely invisible: a delivery is
 * stored durably before iERP AI is acknowledged, a replay is turned into an ack retry rather than a
 * second intake, a paused connection defers rather than drops, and a refused connection keeps the
 * reason without processing anything. All of that was happening with no way to look at it - so a
 * delivery that failed sat in the table with its error and nobody could see it, and the retry
 * endpoint existed with nothing to press.
 *
 * This screen is a trace, not a queue. Once a delivery reaches Completed it belongs to a
 * GatewayDocument and the ordinary Documents, Vendor Invoice Intake and Approvals screens own it
 * from there - so the row links out to the document rather than trying to become a second place to
 * review an invoice. What it shows that nothing else can is the part BEFORE the document existed:
 * whether the message was acknowledged, how many attempts it took, and why it stopped if it did.
 */
export function PartnerDeliveriesPage(): JSX.Element {
  const { can } = useAuth();
  const toast = useToast();
  const canPull = can('partners.search');

  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [busy, setBusy] = useState(false);

  const state = useAsync((signal) => partnerDeliveries.state(signal), []);
  const list = useAsync(
    (signal) => partnerDeliveries.list({ status: status || null, page, pageSize }, signal),
    [status, page, pageSize],
  );

  const table = useSortableTable<DeliveryColumn>({
    storageKey: 'partnerDeliveriesColumnWidths',
    columns: {
      from: { minWidth: 160 },
      theirDocument: { minWidth: 160 },
      status: { minWidth: 140 },
      acknowledged: { minWidth: 130 },
      became: { minWidth: 140 },
      received: { minWidth: 130 },
    },
  });

  const pull = async () => {
    setBusy(true);
    try {
      const response = await partnerDeliveries.pull();
      toast.success(
        response.message
          ?? `${response.pulled} pulled · ${response.storedInvoices} stored · ${response.deferred} deferred`
          + ` · ${response.replays} replay(s) · ${response.terminalRejected} refused.`,
      );
      await list.reload();
      await state.reload();
    } catch (caught) {
      toast.error(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const retry = async (id: string) => {
    setBusy(true);
    try {
      await partnerDeliveries.retry(id);
      toast.success('Put back in line. The replay guard makes this safe to repeat.');
      await list.reload();
    } catch (caught) {
      toast.error(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Partner Deliveries"
        description="Invoices that arrived from trading partners, and the document each one became."
        actions={
          canPull ? (
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void pull()}>
              {busy ? 'Pulling…' : 'Pull now'}
            </button>
          ) : undefined
        }
      />

      <Panel title="The exchange channel">
        {state.loading ? (
          <CardSkeleton />
        ) : state.error ? (
          <ErrorState message="The channel state could not be read." detail={state.error} onRetry={state.reload} />
        ) : state.data ? (
          <>
            <Toolbar>
              <Chip tone={state.data.deliveryMethodConfigured ? 'good' : 'warn'}>
                {state.data.deliveryMethodConfigured ? 'Configured' : 'Not configured'}
              </Chip>
              {state.data.lastPullUtc ? (
                <Chip tone="neutral">last pull {formatRelative(state.data.lastPullUtc)}</Chip>
              ) : (
                <Chip tone="warn">never pulled</Chip>
              )}
            </Toolbar>

            {/*
              The last pull message is shown verbatim. "Pulled 0" is the normal state for a company
              nobody has sent anything to, and it reads very differently from a pull that failed -
              which is exactly the distinction an operator needs and cannot get from a chip.
            */}
            {state.data.lastPullMessage ? (
              <p className="muted">{state.data.lastPullMessage}</p>
            ) : null}

            {!state.data.deliveryMethodConfigured ? (
              <p className="muted">
                The delivery channel has not been configured with iERP AI for this company, so
                nothing can arrive yet. Pulling configures it.
              </p>
            ) : (
              <p className="drawer-note">
                Configured{' '}
                {state.data.deliveryMethodConfiguredUtc
                  ? formatDateTime(state.data.deliveryMethodConfiguredUtc)
                  : '—'}
                . The gateway also pulls on its own schedule; Pull now is for when you do not want
                to wait.
              </p>
            )}
          </>
        ) : null}
      </Panel>

      <Panel title="Deliveries">
        <Toolbar>
          <SelectField
            label="Status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            options={STATUS_OPTIONS}
            placeholder="All statuses"
            className="toolbar-grow"
          />
        </Toolbar>

        {list.loading && !list.data ? (
          <TableSkeleton rows={6} columns={6} />
        ) : list.error ? (
          <ErrorState message="Deliveries could not be read." detail={list.error} onRetry={list.reload} />
        ) : !list.data || list.data.items.length === 0 ? (
          <EmptyState
            title={status ? `No deliveries with status ${status}` : 'Nothing has arrived yet'}
            description={
              status
                ? 'Clear the filter to see every delivery.'
                : 'A delivery appears here when a connected partner sends this company an invoice. An accepted connection and a bound QuickBooks vendor are needed first.'
            }
          />
        ) : (
          <>
            <TableWrap busy={list.loading} tableClassName="data-table-sortable">
              <colgroup>
                <col style={table.columnStyle('from')} />
                <col style={table.columnStyle('theirDocument')} />
                <col style={table.columnStyle('status')} />
                <col style={table.columnStyle('acknowledged')} />
                <col style={table.columnStyle('became')} />
                <col style={table.columnStyle('received')} />
                <col className="col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <SortableTh
                    label="From"
                    sortKey="from"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('from')}
                    onResizeStart={table.beginResize('from')}
                  />
                  <SortableTh
                    label="Their document"
                    sortKey="theirDocument"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('theirDocument')}
                    onResizeStart={table.beginResize('theirDocument')}
                  />
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('status')}
                    onResizeStart={table.beginResize('status')}
                  />
                  <SortableTh
                    label="Acknowledged"
                    sortKey="acknowledged"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('acknowledged')}
                    onResizeStart={table.beginResize('acknowledged')}
                  />
                  <SortableTh
                    label="Became"
                    sortKey="became"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('became')}
                    onResizeStart={table.beginResize('became')}
                  />
                  <SortableTh
                    label="Received"
                    sortKey="received"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('received')}
                    onResizeStart={table.beginResize('received')}
                  />
                  <th scope="col" className="col-actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {table.sort(list.data.items, deliverySortValue).map((d) => (
                  <tr key={d.id}>
                    <td>
                      <span className="cell-strong">
                        {d.counterpartyCompanyName ?? d.sellerCompanyName ?? 'A connected partner'}
                      </span>
                      <span className="cell-sub">
                        {d.deliveryType} · <span className="mono">{d.deliveryId}</span>
                      </span>
                    </td>
                    <td>
                      {d.postedSalesInvoiceNo ?? d.bcSourceDocumentNo ?? '—'}
                      <span className="cell-sub">
                        {d.bcSourceDocumentType ?? ''}
                        {d.pdfFileName ? ` · ${d.pdfFileName}` : ''}
                      </span>
                    </td>
                    <td>
                      <Chip tone={statusTone(d.status)}>{d.status}</Chip>
                      {d.attemptCount > 1 ? (
                        <span className="cell-sub">{d.attemptCount} attempts</span>
                      ) : null}
                      {d.statusMessage ? <span className="cell-sub">{d.statusMessage}</span> : null}
                    </td>
                    <td>
                      {/*
                        Worth its own column: an unacknowledged delivery WILL be re-served by iERP
                        AI, and that is the mechanism working rather than a fault. Hiding it would
                        make a normal re-serve look like a duplicate.
                      */}
                      {d.acknowledged ? (
                        <Chip tone="good">acked</Chip>
                      ) : (
                        <Chip tone="warn">not acked</Chip>
                      )}
                      <span className="cell-sub">
                        {d.ackedUtc ? formatRelative(d.ackedUtc) : 'iERP AI will re-serve it'}
                      </span>
                    </td>
                    <td>
                      {d.gatewayDocumentId ? (
                        <Link className="cell-link" to={`/purchase-invoices?document=${d.gatewayDocumentId}`}>
                          {d.qboDocNumber ? `Bill ${d.qboDocNumber}` : 'the document'}
                        </Link>
                      ) : (
                        <span className="muted">—</span>
                      )}
                      <span className="cell-sub">
                        {d.documentStatus ?? (d.gatewayDocumentId ? '' : 'no document yet')}
                        {d.qboEntityId ? ` · QuickBooks ${d.qboEntityId}` : ''}
                      </span>
                    </td>
                    <td>
                      {formatRelative(d.receivedUtc)}
                      <span className="cell-sub">
                        {d.processedUtc ? `processed ${formatRelative(d.processedUtc)}` : ''}
                      </span>
                    </td>
                    <td className="col-actions">
                      {canPull && canRetry(d.status) ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() => void retry(d.id)}
                        >
                          Retry
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}
      </Panel>
    </>
  );
}
